import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Platform from "react-native-web/dist/exports/Platform";
import { Portal } from '@gorhom/portal';
import { useCopy, copyPropTypes, getTokensPropType, variantProp } from '../utils';
import { useViewport } from '../ViewportProvider';
import { useThemeTokens } from '../ThemeProvider';
import dictionary from './dictionary';
import Card from '../Card';
import IconButton from '../IconButton';
import useDetectOutsideClick from '../utils/useDetectOutsideClick';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const staticStyles = StyleSheet.create({
  positioner: {
    flex: 1,
    // Grow to maxWidth when possible, shrink when not possible
    zIndex: 10000,
    // Position on top of all the other overlays, including backdrops and modals,
    elevation: 10000,
    // Position on top of all the other overlays, including backdrops and modals,
    borderRadius: 4,
    ...Platform.select({
      web: {
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 8px 0px'
      },
      default: {
        shadowColor: 'rgba(0, 0, 0, 0.1)',
        shadowOffset: {
          width: 0,
          height: 4
        },
        shadowOpacity: 1,
        shadowRadius: 8
      }
    })
  },
  card: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0
  },
  closeButtonContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1
  },
  hidden: {
    // Use opacity not visibility to hide the dropdown during positioning
    // so on web, children may be focused from the first render
    opacity: 0
  }
});
const selectContainerStyle = (enableFullscreen, themeTokens) => ({
  borderColor: themeTokens.borderColor,
  backgroundColor: themeTokens.backgroundColor,
  ...Platform.select({
    web: {
      position: enableFullscreen ? 'fixed' : 'absolute'
    },
    default: {
      position: 'absolute'
    }
  })
});
const selectCloseButtonContainerStyles = _ref => {
  let {
    paddingRight,
    paddingTop
  } = _ref;
  return {
    paddingRight,
    paddingTop
  };
};
const ModalOverlay = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
    children,
    isReady = false,
    maxHeight,
    maxHeightSize,
    maxWidthSize,
    minHeight,
    minWidth,
    overlaidPosition,
    onLayout,
    variant,
    tokens,
    copy,
    onClose,
    enableFullscreen = false,
    dismissWhenPressedOutside = false
  } = _ref2;
  const viewport = useViewport();
  const themeTokens = useThemeTokens('Modal', tokens, variant, {
    viewport,
    maxWidth: false
  });
  const containerRef = React.useRef(ref || null);
  useDetectOutsideClick(containerRef, onClose, dismissWhenPressedOutside);
  const containerWidthHeight = {
    minWidth: tokens.maxWidth ? maxWidthSize : minWidth,
    minHeight: maxHeight ? maxHeightSize : minHeight,
    maxWidth: maxWidthSize,
    maxHeight: maxHeightSize
  };
  const {
    closeIcon: CloseIconComponent,
    backgroundColor
  } = themeTokens;
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const closeLabel = getCopy('closeButton');
  return /*#__PURE__*/_jsx(Portal, {
    children: /*#__PURE__*/_jsx(View, {
      ref: containerRef,
      onLayout: onLayout,
      style: [overlaidPosition, containerWidthHeight, staticStyles.positioner, !isReady && staticStyles.hidden, selectContainerStyle(enableFullscreen, themeTokens)],
      children: /*#__PURE__*/_jsxs(Card, {
        tokens: {
          ...staticStyles.card,
          backgroundColor
        },
        children: [/*#__PURE__*/_jsx(View, {
          style: [staticStyles.closeButtonContainer, selectCloseButtonContainerStyles(themeTokens)],
          children: /*#__PURE__*/_jsx(IconButton, {
            onPress: onClose,
            icon: CloseIconComponent,
            accessibilityRole: "button",
            accessibilityLabel: closeLabel
          })
        }), children]
      })
    })
  });
});
ModalOverlay.displayName = 'ModalOverlay';
ModalOverlay.propTypes = {
  children: PropTypes.node.isRequired,
  isReady: PropTypes.bool,
  overlaidPosition: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    width: PropTypes.number
  }),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeightSize: PropTypes.number,
  maxWidthSize: PropTypes.number,
  maxHeight: PropTypes.bool,
  maxWidth: PropTypes.bool,
  onLayout: PropTypes.func,
  variant: variantProp.propType,
  tokens: getTokensPropType('Modal'),
  copy: copyPropTypes,
  onClose: PropTypes.func,
  enableFullscreen: PropTypes.bool,
  /**
   * If true, clicking outside the content will trigger the a close callback, dismissing the content.
   * @deprecated This parameter will be removed in the next major release; detection will be always enabled by default.
   */
  dismissWhenPressedOutside: PropTypes.bool
};
export default ModalOverlay;